// ============================================================
// FONADHOO CRM v3.0 - HOUSEHOLD VIEW
// ============================================================
// Search-first browse: smart search box + collapsible Location filter
// group + active pills + empty state. Mirrors the Advanced Search
// pattern so users learn one mental model that works across the app.
//
// A "household" is the set of voters sharing the same (island, house).
// Filters narrow which voters get grouped before grouping happens.
// ============================================================

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePhotoUrls } from './shared/usePhotoUrl';
import { calculateAge } from './shared/utils';
import SearchableSelect from './shared/SearchableSelect';
import { Icon } from './shared/Icon';
import { hasEffectivePermission } from './shared/constants';

// Support-level color palette — matches the rest of the app.
const SC = {
  'Strong Support':  { bg: 'var(--sup-strong-bg)',       color: 'var(--sup-strong-text)' },
  'Leaning Support': { bg: 'var(--sup-lean-bg)',         color: 'var(--sup-lean-text)' },
  'Undecided':       { bg: 'var(--sup-undecided-bg)',    color: 'var(--sup-undecided-text)' },
  'Leaning Against': { bg: 'var(--sup-against-lean-bg)', color: 'var(--sup-against-lean-text)' },
  'Strong Against':  { bg: 'var(--sup-against-bg)',      color: 'var(--sup-against-text)' },
};

const isSupporter = (v) => v.support === 'Strong Support' || v.support === 'Leaning Support';
const isAgainst = (v) => v.support === 'Strong Against' || v.support === 'Leaning Against';

// Household-level priority classification (computed on the grouped result).
const PRIORITY_FILTERS = [
  { key: 'All',        label: 'All' },
  { key: 'undecided',  label: 'Has undecided' },
  { key: 'supporter',  label: 'Supporter-majority' },
  { key: 'against',    label: 'Has against' },
  { key: 'swing',      label: 'Swing (mixed)' },
];
const matchesPriority = (h, priority) => {
  if (priority === 'All') return true;
  const total = h.voters.length;
  const sup = h.voters.filter(isSupporter).length;
  const agn = h.voters.filter(isAgainst).length;
  const und = total - sup - agn;
  if (priority === 'undecided') return und > 0;                              // any persuadable voter
  if (priority === 'supporter') return total > 0 && sup > total / 2;          // leans your way (not "every member")
  if (priority === 'against')   return agn > 0;                              // any opponent voter
  if (priority === 'swing')     return [sup, und, agn].filter(n => n > 0).length >= 2; // mixed household — worth a visit
  return true;
};

const EMPTY_CRITERIA = {
  searchText: '',
  island: 'All',
  ward: 'All',
  house: 'All',
  priority: 'All',
};

const inputStyle = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 7,
  border: '1px solid var(--border)',
  background: 'var(--surface-2)',
  color: 'var(--text-strong)',
  fontSize: 12,
  fontFamily: "'DM Mono', monospace",
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  fontSize: 10,
  marginBottom: 5,
  color: 'var(--text-2)',
  fontFamily: "'DM Mono', monospace",
};

// ────────────────────────────────────────────────────────────────
// FilterGroup — collapsible card. Mirrors AdvancedSearch's version.
// ────────────────────────────────────────────────────────────────
function FilterGroup({ icon, title, activeCount, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid ' + (activeCount > 0 ? '#3b82f6' : 'var(--border)'),
      borderRadius: 8,
      marginBottom: 8,
      overflow: 'visible',
      transition: 'border-color 0.15s',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '12px 14px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          color: 'inherit',
        }}
      >
        <span style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 14,
          color: 'var(--link)',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <Icon name={icon} size={15} color="var(--link)" />
          <span>{title}</span>
          {activeCount > 0 && (
            <span style={{
              fontSize: 9,
              padding: '2px 7px',
              borderRadius: 10,
              background: 'var(--border)',
              color: 'var(--link)',
              border: '1px solid #3b82f6',
              fontFamily: "'DM Mono', monospace",
              fontWeight: 500,
            }}>
              {activeCount} active
            </span>
          )}
        </span>
        <span style={{
          color: 'var(--text-3)',
          fontSize: 14,
          lineHeight: 1,
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s',
        }}>▾</span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px 14px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ────────────────────────────────────────────────────────────────
export default function HouseholdView({ supabase, user, allVoters = [] }) {
  const profile = user ?? null;
  const canSeeSupport = hasEffectivePermission(profile, 'canViewVotes');
  const isSimpleMode = !hasEffectivePermission(profile, 'canViewAll');
  const navigate = useNavigate();
  const canCanvass = hasEffectivePermission(profile, 'canViewAll') || hasEffectivePermission(profile, 'canLogOutreach');
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [criteria, setCriteria] = useState(EMPTY_CRITERIA);

  // ── Canvass status (household_status table, keyed by island|house) ──
  const hkey = (island, house) => `${island}|${house}`;
  const [statuses, setStatuses] = useState(() => new Map());
  const [noteDraft, setNoteDraft] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  useEffect(() => {
    let alive = true;
    supabase.from('household_status').select('island, house, visited, note, visited_at').then(({ data, error }) => {
      if (!alive) return;
      if (error) { console.error('[household_status] load failed:', error); return; }
      if (!data) return;
      setStatuses(new Map(data.map(r => [hkey(r.island, r.house), r])));
    });
    return () => { alive = false; };
  }, [supabase]);

  const saveStatus = async (household, patch) => {
    const key = hkey(household.island, household.house);
    const prev = statuses.get(key) || {};
    const row = {
      island: household.island,
      house: household.house,
      visited: patch.visited != null ? patch.visited : (prev.visited || false),
      note: patch.note != null ? patch.note : (prev.note || null),
      visited_at: (patch.visited != null ? patch.visited : prev.visited) ? new Date().toISOString() : null,
      visited_by: profile?.id || null,
      updated_at: new Date().toISOString(),
    };
    setSavingStatus(true);
    const { error } = await supabase.from('household_status').upsert(row, { onConflict: 'island,house' });
    setSavingStatus(false);
    if (error) { alert('Could not save canvass status: ' + error.message); return; }
    setStatuses(prev2 => { const n = new Map(prev2); n.set(key, { ...row }); return n; });
  };

  // ────────────────────────────────────────────────────────────
  // Cascading Location options — same approach as AdvancedSearch.
  // Wards depend on Island; Houses depend on Island+Ward.
  // ────────────────────────────────────────────────────────────
  const islands = useMemo(() => {
    const s = new Set();
    for (const v of allVoters) if (v.island) s.add(v.island);
    return Array.from(s).sort();
  }, [allVoters]);

  const wardsForIsland = useMemo(() => {
    const s = new Set();
    for (const v of allVoters) {
      if (criteria.island !== 'All' && v.island !== criteria.island) continue;
      if (v.ward) s.add(v.ward);
    }
    return Array.from(s).sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true })
    );
  }, [allVoters, criteria.island]);

  const housesForLocation = useMemo(() => {
    const s = new Set();
    for (const v of allVoters) {
      if (criteria.island !== 'All' && v.island !== criteria.island) continue;
      if (criteria.ward !== 'All' && v.ward !== criteria.ward) continue;
      if (v.house) s.add(v.house);
    }
    return Array.from(s).sort();
  }, [allVoters, criteria.island, criteria.ward]);

  // Cascade-aware setters: changing Island clears Ward + House;
  // changing Ward clears House.
  const setField = (key, value) => setCriteria(c => ({ ...c, [key]: value }));
  const setIsland = (value) =>
    setCriteria(c => ({ ...c, island: value, ward: 'All', house: 'All' }));
  const setWard = (value) =>
    setCriteria(c => ({ ...c, ward: value, house: 'All' }));

  // ────────────────────────────────────────────────────────────
  // Active pills — drives the activeCount badges and the pill row.
  // ────────────────────────────────────────────────────────────
  const activePills = useMemo(() => {
    const pills = [];
    if (criteria.searchText.trim()) {
      pills.push({ key: 'searchText', label: `Search: "${criteria.searchText.trim()}"` });
    }
    if (criteria.island !== 'All') pills.push({ key: 'island', label: `Island: ${criteria.island}` });
    if (criteria.ward !== 'All')   pills.push({ key: 'ward',   label: `Ward: ${criteria.ward}` });
    if (criteria.house !== 'All')  pills.push({ key: 'house',  label: `House: ${criteria.house}` });
    if (criteria.priority !== 'All') pills.push({ key: 'priority', label: PRIORITY_FILTERS.find(p => p.key === criteria.priority)?.label || criteria.priority });
    return pills;
  }, [criteria]);

  const hasAnyCriteria = activePills.length > 0 || isSimpleMode;
  const locationActiveCount = activePills.filter(p =>
    p.key === 'island' || p.key === 'ward' || p.key === 'house'
  ).length;

  const removePill = (pill) => {
    if (pill.key === 'searchText') return setField('searchText', '');
    if (pill.key === 'island')     return setIsland('All');
    if (pill.key === 'ward')       return setWard('All');
    if (pill.key === 'house')      return setField('house', 'All');
    if (pill.key === 'priority')   return setField('priority', 'All');
  };

  // Build CSV / printable canvass sheet from the current results.
  const exportCsv = () => {
    const esc = (s) => `"${String(s == null ? '' : s).replace(/"/g, '""')}"`;
    const rows = [['House', 'Island', 'Ward', 'Voters', 'Supporters', 'Undecided', 'Against', 'Members']];
    for (const h of groupedHouseholds) {
      const sup = h.voters.filter(isSupporter).length;
      const agn = h.voters.filter(isAgainst).length;
      const und = h.voters.length - sup - agn;
      rows.push([h.house, h.island, h.ward || '', h.voters.length, sup, und, agn, h.voters.map(v => v.name).join('; ')].map(esc));
    }
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `households-${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const printSheet = () => {
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const win = window.open('', '_blank', 'width=900,height=1000');
    if (!win) { alert('Please allow pop-ups to print the canvass sheet.'); return; }
    const blocks = groupedHouseholds.map(h => {
      const members = h.voters.map(v => {
        const lvl = canSeeSupport && v.support ? ` <span class="lvl">(${esc(v.support)})</span>` : '';
        return `<li><span class="box">&#9744;</span> ${esc(v.name)}${lvl}</li>`;
      }).join('');
      return `<div class="hh"><h3>${esc(h.house)} <span class="loc">${esc(h.island)}${h.ward ? ' · ' + esc(h.ward) : ''} · ${h.voters.length} voters</span></h3><ul>${members}</ul></div>`;
    }).join('');
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Canvass sheet</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#0f172a;margin:32px}
  h1{font-size:20px;margin:0 0 4px} .sub{font-size:12px;color:#64748b;margin-bottom:18px}
  .hh{break-inside:avoid;border:1px solid #e2e8f0;border-radius:8px;padding:12px 14px;margin-bottom:12px}
  .hh h3{font-size:14px;margin:0 0 8px} .loc{font-weight:400;color:#64748b;font-size:12px}
  ul{list-style:none;margin:0;padding:0} li{font-size:13px;padding:3px 0;display:flex;align-items:center;gap:8px}
  .box{font-size:16px} .lvl{color:#64748b}
  .actions{margin:18px 0;text-align:center} button{font:inherit;padding:8px 18px;border:none;border-radius:8px;background:#2563eb;color:#fff;cursor:pointer}
  @media print{.actions{display:none}body{margin:0}}
</style></head><body>
  <h1>Canvass Sheet</h1>
  <div class="sub">${summary.households} households · ${summary.voters} voters · generated ${new Date().toLocaleDateString()}</div>
  ${blocks}
  <div class="actions"><button onclick="window.print()">Print</button></div>
</body></html>`);
    win.document.close();
  };

  // ────────────────────────────────────────────────────────────
  // Filter voters (only those with a house), then group by
  // island + house. Search matches house name + voter name + id +
  // phone fields. Sort groups by voter count (most populated first).
  // Only runs when there's at least one criterion — otherwise we
  // return [] and show the empty state.
  // ────────────────────────────────────────────────────────────
  const groupedHouseholds = useMemo(() => {
    if (!hasAnyCriteria) return [];

    const search = criteria.searchText.trim().toLowerCase();

    const filtered = allVoters.filter(v => {
      if (!v.house) return false; // Households must have a house name
      if (criteria.island !== 'All' && v.island !== criteria.island) return false;
      if (criteria.ward !== 'All' && v.ward !== criteria.ward) return false;
      if (criteria.house !== 'All' && v.house !== criteria.house) return false;
      if (search) {
        const haystack = [
          v.house, v.name, v.id_number, v.phone, v.phone2, v.phone3, v.whatsapp,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });

    // Group by island + house key (same house name on different islands
    // is a different household).
    const groups = {};
    for (const voter of filtered) {
      const key = `${voter.island}|${voter.house}`;
      if (!groups[key]) {
        groups[key] = {
          island: voter.island,
          ward: voter.ward,
          house: voter.house,
          voters: [],
        };
      }
      groups[key].voters.push(voter);
    }
    return Object.values(groups)
      .filter(h => matchesPriority(h, criteria.priority))
      .sort((a, b) => b.voters.length - a.voters.length);
  }, [allVoters, criteria, hasAnyCriteria]);

  // Summary stats for the current filtered result.
  const summary = useMemo(() => {
    const households = groupedHouseholds.length;
    const voters = groupedHouseholds.reduce((s, h) => s + h.voters.length, 0);
    const supporterMajority = groupedHouseholds.filter(h => {
      const sup = h.voters.filter(isSupporter).length;
      return h.voters.length > 0 && sup > h.voters.length / 2;
    }).length;
    return { households, voters, avg: households ? (voters / households) : 0, supporterMajority };
  }, [groupedHouseholds]);

  // ────────────────────────────────────────────────────────────
  // Photo URL resolution for the currently-viewed household detail.
  // Single batched call avoids one round-trip per member.
  // ────────────────────────────────────────────────────────────
  const householdPhotoValues = useMemo(
    () => (selectedHousehold?.voters || []).map(v => v.photo_url).filter(Boolean),
    [selectedHousehold]
  );
  const householdPhotoMap = usePhotoUrls(supabase, householdPhotoValues);

  // ────────────────────────────────────────────────────────────
  // RENDER — DETAIL VIEW
  // Unchanged from before; reachable when a card is clicked.
  // ────────────────────────────────────────────────────────────
  if (selectedHousehold) {
    const householdVoters = selectedHousehold.voters;
    const st = statuses.get(hkey(selectedHousehold.island, selectedHousehold.house)) || {};
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 16 }}>
        <button
          onClick={() => setSelectedHousehold(null)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '9px 16px',
            marginBottom: 20,
            borderRadius: 9,
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            boxShadow: 'var(--shadow-sm)',
            color: 'var(--text-2)',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: "'DM Mono', monospace",
          }}
        >
          <Icon name="arrow" size={15} style={{ transform: 'rotate(180deg)' }} /> Back to Households
        </button>

        <div style={{
          background: 'var(--surface)',
          borderRadius: 14,
          padding: 24,
          marginBottom: 20,
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-sm)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <span style={{ width: 42, height: 42, borderRadius: 12, display: 'grid', placeItems: 'center', background: '#10b9811f', color: '#10b981', flexShrink: 0 }}>
              <Icon name="home" size={22} />
            </span>
            <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
              {selectedHousehold.house}
            </h2>
          </div>
          <div style={{
            display: 'flex',
            gap: 20,
            fontSize: 14,
            color: 'var(--text-2)',
            marginBottom: 20,
            flexWrap: 'wrap',
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="mapPin" size={14} /> {selectedHousehold.island}{selectedHousehold.ward ? `, ${selectedHousehold.ward}` : ''}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="users" size={14} /> {householdVoters.length} voters</span>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 12,
          }}>
            {canSeeSupport && ['Strong Support', 'Leaning Support', 'Undecided', 'Leaning Against', 'Strong Against'].map(level => {
              const count = householdVoters.filter(v => v.support === level).length;
              const style = SC[level];
              return (
                <div key={level} style={{
                  background: 'var(--surface-2)',
                  padding: 16,
                  borderRadius: 10,
                  textAlign: 'center',
                  border: '1px solid var(--border)',
                }}>
                  <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 6, color: style.color, fontVariantNumeric: 'tabular-nums' }}>
                    {count}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-2)' }}>{level}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Canvass tracking */}
        {(canCanvass || st.visited || st.note) && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: st.visited && st.visited_at ? 6 : 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: 'var(--text)' }}>Canvass</h3>
              {canCanvass && (
                <button onClick={() => saveStatus(selectedHousehold, { visited: !st.visited, note: noteDraft })} disabled={savingStatus}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 9, border: st.visited ? 'none' : '1px solid var(--border)', background: st.visited ? '#10b981' : 'transparent', color: st.visited ? '#fff' : 'var(--text-2)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Mono', monospace" }}>
                  <Icon name={st.visited ? 'check' : 'home'} size={14} color={st.visited ? '#fff' : 'currentColor'} /> {st.visited ? 'Visited' : 'Mark visited'}
                </button>
              )}
            </div>
            {st.visited && st.visited_at && (
              <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: "'DM Mono', monospace", marginBottom: 12 }}>
                Visited {new Date(st.visited_at).toLocaleDateString()}
              </div>
            )}
            {canCanvass ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <textarea value={noteDraft} onChange={e => setNoteDraft(e.target.value)} rows={2}
                  placeholder="Canvass notes (e.g. not home, try evening)…"
                  style={{ flex: 1, minWidth: 220, ...inputStyle, fontSize: 13, padding: '9px 11px', resize: 'vertical', fontFamily: 'inherit' }} />
                <button onClick={() => saveStatus(selectedHousehold, { note: noteDraft })} disabled={savingStatus}
                  style={{ padding: '9px 16px', borderRadius: 9, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {savingStatus ? '…' : 'Save note'}
                </button>
              </div>
            ) : (
              st.note && <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>{st.note}</div>
            )}
          </div>
        )}

        {/* Members grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}>
          {householdVoters.map(voter => {
            const supportStyle = canSeeSupport ? (SC[voter.support] || SC['Undecided']) : SC['Undecided'];
            const resolvedPhoto = voter.photo_url ? householdPhotoMap.get(voter.photo_url) : null;
            return (
              <div
                key={voter.id}
                onClick={() => navigate(`/voter/${voter.id}`)}
                style={{
                  background: 'var(--surface)',
                  borderRadius: 12,
                  padding: 20,
                  cursor: 'pointer',
                  border: '1px solid var(--border)',
                  boxShadow: 'var(--shadow-sm)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{
                    width: 45,
                    height: 45,
                    borderRadius: '50%',
                    background: resolvedPhoto ? `url(${resolvedPhoto}) center/cover` : 'var(--border-2)',
                    border: `2px solid ${supportStyle.color}`,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontWeight: 700,
                    color: 'var(--text-2)',
                  }}>
                    {!resolvedPhoto && (voter.name || '').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {voter.name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
                      Age {voter.age ?? calculateAge(voter.dob) ?? '—'} • {voter.gender || '—'}
                    </div>
                  </div>
                </div>
                {canSeeSupport && voter.support && (
                  <div style={{
                    display: 'inline-block',
                    padding: '3px 9px',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 600,
                    background: supportStyle.bg,
                    color: supportStyle.color,
                  }}>
                    {voter.support}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────
  // RENDER — LIST VIEW
  // ────────────────────────────────────────────────────────────
  return (
    <div>

      {/* Header + Smart Search Box (boxed header — consistent with Events / Trips / Outreach) */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-sm)',
        borderRadius: 14,
        padding: 20,
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 16 }}>
          <span style={{ width: 38, height: 38, borderRadius: 11, display: 'grid', placeItems: 'center', background: '#10b9811f', color: '#10b981', flexShrink: 0 }}>
            <Icon name="building" size={20} />
          </span>
          <div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 2 }}>Directory</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', fontWeight: 600, margin: 0, color: 'var(--text-strong)', letterSpacing: '-0.3px', lineHeight: 1 }}>Households</h2>
          </div>
        </div>
        <input
          type="text"
          value={criteria.searchText}
          onChange={(e) => setField('searchText', e.target.value)}
          placeholder="Search by house name, voter name, ID or phone…"
          style={{ ...inputStyle, fontSize: 13, padding: '11px 13px' }}
        />
      </div>

      {/* Filter Groups — just Location for now, kept simple. */}
      <FilterGroup icon="mapPin" title="Location" activeCount={locationActiveCount}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <div>
            <label style={labelStyle}>Island</label>
            <SearchableSelect
              value={criteria.island === 'All' ? '' : criteria.island}
              options={islands}
              onChange={v => setIsland(v || 'All')}
              placeholder="All Islands"
            />
          </div>
          <div>
            <label style={labelStyle}>
              Ward {criteria.island === 'All' && <span style={{ color: 'var(--text-4)' }}>(pick island first)</span>}
            </label>
            <SearchableSelect
              value={criteria.ward === 'All' ? '' : criteria.ward}
              options={wardsForIsland}
              onChange={v => setWard(v || 'All')}
              placeholder="All Wards"
              disabled={criteria.island === 'All'}
            />
          </div>
          <div>
            <label style={labelStyle}>House</label>
            <SearchableSelect
              value={criteria.house === 'All' ? '' : criteria.house}
              options={housesForLocation}
              onChange={v => setField('house', v || 'All')}
              placeholder="All Houses"
              disabled={housesForLocation.length === 0}
            />
          </div>
        </div>
      </FilterGroup>

      {/* Priority household filters (depend on support data) */}
      {canSeeSupport && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {PRIORITY_FILTERS.map(p => {
            const on = criteria.priority === p.key;
            return (
              <button key={p.key} onClick={() => setField('priority', p.key)} style={{
                padding: '8px 16px', borderRadius: 9,
                border: on ? '1px solid rgba(59,130,246,0.55)' : '1px solid var(--border)',
                background: on ? 'rgba(59,130,246,0.13)' : 'transparent',
                color: on ? '#60a5fa' : 'var(--text-2)',
                fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Mono', monospace",
                transition: 'background 0.15s, border-color 0.15s, color 0.15s',
              }}
                onMouseEnter={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text)'; } }}
                onMouseLeave={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; } }}
              >{p.label}</button>
            );
          })}
        </div>
      )}

      {/* Active filter pills + Reset */}
      {hasAnyCriteria && (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 10,
          marginTop: 4,
          marginBottom: 12,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'center',
        }}>
          {activePills.map(pill => (
            <button
              key={pill.key}
              onClick={() => removePill(pill)}
              style={{
                padding: '4px 9px',
                borderRadius: 14,
                background: 'var(--border)',
                border: '1px solid #3b82f6',
                color: 'var(--link)',
                cursor: 'pointer',
                fontSize: 10,
                fontFamily: "'DM Mono', monospace",
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}>
              <span>{pill.label}</span>
              <span style={{ color: 'var(--link)', fontWeight: 700 }}>×</span>
            </button>
          ))}
          <button
            onClick={() => setCriteria(EMPTY_CRITERIA)}
            style={{
              padding: '4px 9px',
              borderRadius: 14,
              background: 'transparent',
              border: '1px dashed #ef4444',
              color: '#ef4444',
              cursor: 'pointer',
              fontSize: 10,
              fontFamily: "'DM Mono', monospace",
              marginLeft: 'auto',
            }}>
            Reset all
          </button>
        </div>
      )}

      {/* Results / Empty state */}
      {!hasAnyCriteria ? (
        <div style={{
          background: 'var(--surface)',
          border: '1px dashed var(--border)',
          borderRadius: 8,
          padding: '40px 20px',
          textAlign: 'center',
        }}>
          <div style={{ display: 'inline-grid', placeItems: 'center', width: 56, height: 56, borderRadius: 14, background: 'var(--surface-2)', color: 'var(--text-4)', marginBottom: 12 }}><Icon name="building" size={26} /></div>
          <div style={{
            color: 'var(--text)',
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 17,
            marginBottom: 4,
          }}>
            Pick a location filter or search above
          </div>
          <div style={{
            color: 'var(--text-3)',
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
          }}>
            Households will appear here once you narrow down by island, ward, house, or search.
          </div>
        </div>
      ) : (
        <>
          {groupedHouseholds.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 14 }}>
              {[
                { value: summary.households, label: 'Households', color: '#10b981', icon: 'building' },
                { value: summary.voters, label: 'Voters', color: '#3b82f6', icon: 'users' },
                { value: summary.avg.toFixed(1), label: 'Avg size', color: '#8b5cf6', icon: 'home' },
                ...(canSeeSupport ? [{ value: summary.supporterMajority, label: 'Supporter-majority', color: '#10b981', icon: 'check' }] : []),
              ].map(({ value, label, color, icon }) => (
                <div key={label} style={{ background: 'var(--surface-2)', padding: '14px 16px', borderRadius: 12, minHeight: 96, position: 'relative', overflow: 'hidden', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-2)', fontWeight: 500, lineHeight: 1.2 }}>{label}</span>
                    <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: color + '1f', color, flexShrink: 0 }}>
                      <Icon name={icon} size={15} />
                    </span>
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px' }}>{value}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '4px 2px 10px 2px' }}>
            <div style={{ fontSize: 11, color: 'var(--text-2)', fontFamily: "'DM Mono', monospace" }}>
              Showing <strong style={{ color: 'var(--link)' }}>{groupedHouseholds.length}</strong> households
            </div>
            {groupedHouseholds.length > 0 && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={exportCsv} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Mono', monospace" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; }}>
                  <Icon name="download" size={14} /> Export CSV
                </button>
                <button onClick={printSheet} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Mono', monospace" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; }}>
                  <Icon name="vote" size={14} /> Print sheet
                </button>
              </div>
            )}
          </div>

          {groupedHouseholds.length === 0 ? (
            <div style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 30,
              textAlign: 'center',
              color: 'var(--text-3)',
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
            }}>
              No households match these filters. Try removing a pill above.
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 14,
            }}>
              {groupedHouseholds.map((household, idx) => {
                const total = household.voters.length;
                const sup = household.voters.filter(v => v.support === 'Strong Support' || v.support === 'Leaning Support').length;
                const agn = household.voters.filter(v => v.support === 'Strong Against' || v.support === 'Leaning Against').length;
                const und = total - sup - agn;
                const pct = (n) => total > 0 ? (n / total * 100) : 0;
                const visited = statuses.get(hkey(household.island, household.house))?.visited;
                return (
                  <div
                    key={`${household.island}-${household.house}-${idx}`}
                    onClick={() => { setSelectedHousehold(household); setNoteDraft(statuses.get(hkey(household.island, household.house))?.note || ''); }}
                    style={{
                      background: 'var(--surface)',
                      borderRadius: 12,
                      padding: 18,
                      cursor: 'pointer',
                      border: '1px solid var(--border)',
                      boxShadow: 'var(--shadow-sm)',
                      transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#3b82f6';
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 12 }}>
                      <span style={{ width: 36, height: 36, borderRadius: 10, display: 'grid', placeItems: 'center', background: '#10b9811f', color: '#10b981', flexShrink: 0 }}>
                        <Icon name="home" size={18} />
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{household.house}</span>
                          {visited && <span title="Canvassed" style={{ flexShrink: 0, width: 16, height: 16, borderRadius: '50%', background: '#10b981', display: 'grid', placeItems: 'center' }}><Icon name="check" size={11} color="#fff" /></span>}
                        </h3>
                        <div style={{ fontSize: 11.5, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                          <Icon name="mapPin" size={12} /> {household.island}{household.ward ? `, ${household.ward}` : ''}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: canSeeSupport ? 10 : 0 }}>
                      <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-strong)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{total}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: "'DM Mono', monospace" }}>{total === 1 ? 'voter' : 'voters'}</span>
                    </div>

                    {canSeeSupport && (
                      <>
                        <div style={{ display: 'flex', height: 6, borderRadius: 4, overflow: 'hidden', background: 'var(--surface-2)' }}>
                          {sup > 0 && <div style={{ width: `${pct(sup)}%`, background: '#10b981' }} />}
                          {und > 0 && <div style={{ width: `${pct(und)}%`, background: 'var(--text-3)' }} />}
                          {agn > 0 && <div style={{ width: `${pct(agn)}%`, background: '#ef4444' }} />}
                        </div>
                        <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 11, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 7, height: 7, borderRadius: 2, background: '#10b981' }} /> {sup}</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 7, height: 7, borderRadius: 2, background: 'var(--text-3)' }} /> {und}</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 7, height: 7, borderRadius: 2, background: '#ef4444' }} /> {agn}</span>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
